import React, { useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { LngLat } from "mapbox-gl";
import { Button } from "Components/ReactstrapSlim";
import { ModuleCaption } from "Components/Module";
import { useModulePager } from "Hooks";
import { CollectionsClient, VenueSearchResult } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import LoadingPlaceholder from "./LoadingPlaceholder";
import { SearchContext } from "../../Main/SearchContextProvider";
import ModulePager from "../../ModulePager";
import ModulePagerControls from "../../ModulePager/ModulePagerControls";
import { VenueCard } from "../../VenueCard";

interface FavoritesTabProps {
   onVenueCardClick: (e: React.SyntheticEvent, venue: VenueSearchResult) => void;
   controlsElement: HTMLElement | null;
}

const FavoritesTab: React.FC<FavoritesTabProps> = ({ onVenueCardClick, controlsElement }) => {
   const [favorites, setFavorites] = useState<VenueSearchResult[]>([]);
   const collectionClient = useApiClient(CollectionsClient);
   const searchManager = useContext(SearchContext);
   const [isLoading, setLoading] = useState(true);

   const favoritesPager = useModulePager({
      itemCount: favorites.length,
      itemWidth: 256,
      gapWidth: 32
   });

   const loadFavorites = async (abortSignal: AbortSignal) => {
      try {
         setLoading(true);
         setFavorites(orderFavoritesByLocation(await collectionClient.memberVenues(true, abortSignal), searchManager.geoLocation));
      }
      finally {
         setLoading(false);
      }
   }

   const orderFavoritesByLocation = (favs: VenueSearchResult[], location?: { latitude: number | undefined, longitude: number | undefined }): VenueSearchResult[] => {
      if (location && location.latitude !== undefined && location.longitude !== undefined) {
         const searchCenter = new LngLat(location.longitude, location.latitude);
         return favs.sort((a, b) => {
            const lngLatA = new LngLat(a.longitude || 0, a.latitude || 0);
            const lngLatB = new LngLat(b.longitude || 0, b.latitude || 0);
            const distanceToA = searchCenter.distanceTo(lngLatA);
            const distanceToB = searchCenter.distanceTo(lngLatB);

            return distanceToA > distanceToB ? 1 : distanceToA < distanceToB ? -1 : 0;
         });
      } else {
         return favs;
      }
   }

   useEffect(() => {
      const abortController = new AbortController();
      loadFavorites(abortController.signal);
      return function cleanup() {
         abortController.abort();
      }
   }, []);

   useEffect(() => {
      favoritesPager.reset();
   }, [favorites]);

   useEffect(() => {
      setFavorites(orderFavoritesByLocation(favorites, searchManager.geoLocation));
   }, [searchManager.geoLocation])

   return <div className="tap-pane active">
      {
         controlsElement != null
            ? createPortal(<ModulePagerControls pager={favoritesPager} />, controlsElement)
            : null
      }

      {
         isLoading
            ? <LoadingPlaceholder />
            : !favorites.length
               ? <div className="explore-favorites-null-state">
                  <ModuleCaption className="mb-px8">You don't have any favorite spaces yet</ModuleCaption>
                  <div className="mb-px24">Explore spaces nearby and click the heart icon to add to your favorites</div>
                  <Button color="arctic-20" size="sm" onClick={() => window.open(searchManager.currentSearchLink(), "_blank")}>Go to search results</Button>
               </div>
               : <ModulePager
                  pager={favoritesPager}
                  items={favorites}
                  renderItem={(venue, style) =>
                     <VenueCard
                        key={venue.venueId}
                        venue={venue}
                        onClick={onVenueCardClick}
                        style={style}
                     />}
               />
      }
   </div>;
}

export default FavoritesTab;